'use client';

import { FC, useEffect, useState } from 'react';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { DistanceCalculatorService } from '@/hotdesk-ui/services/distance.calculator.service';
import { NumberFormatService } from '@/hotdesk-ui/services/number-format.service';
import { currencyConvert } from '@/hotdesk-ui/helpers/utils';
import CardOnMap from '@/hotdesk-ui/components/card-on-map/CardOnMap';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { useWebReviewsDisplay } from '@/hotdesk-ui/hooks/useWebReviewsDisplay';
const PRICE_KEYS = ['hour', 'day', 'month', 'annual'] as const;
type PriceKey = typeof PRICE_KEYS[number];
const pickPriceUnit = (price: Record<string, unknown> | undefined): PriceKey => {
  if (!price) return 'annual';
  const keys = Object.keys(price);
  return (PRICE_KEYS.find(k => keys.includes(k)) ?? 'annual') as PriceKey;
};
const calculateRating = (rating: number) => {
  const result = rating / 100 * 5;
  if (result > 5) return 5;
  if (result < 1) return 1;
  return result;
};
export interface LocationMapCardProps {
  item: MyListing;
  type: 'workspace' | 'listing';
  url: string;
  lat: number;
  lng: number;
}
const LocationMapCard: FC<LocationMapCardProps> = ({
  item,
  type,
  url,
  lat,
  lng
}) => {
  const defaultCurrency = useAppSelector((state: RootState) => state.currency.defaultCurrency);
  const currencyList = useAppSelector((state: RootState) => state.currency.currency);
  const languageCode = useAppSelector((state: RootState) => state.listing.defaultLanguage);
  const priceUnit = pickPriceUnit(item.rules?.price as Record<string, unknown> | undefined);
  const [price, setPrice] = useState('');
  const [baseCurrencyName, setBaseCurrencyName] = useState('');
  const [distance, setDistance] = useState(0);
  const [getdistance, setGetdistance] = useState(false);
  const [listingRating, setListingRating] = useState(0);
  const ratingFeature = useWebReviewsDisplay(type === 'workspace' ? 'workspace_details' : 'listing_details');
  useEffect(() => {
    if (!lat || !lng) return;
    const distanceService = new DistanceCalculatorService();
    distanceService.calculateDistanceFromCurrentLocation(lat, lng).then(({
      success,
      distance: d
    }) => {
      if (success) {
        setDistance(d);
        setGetdistance(true);
      } else {
        setGetdistance(false);
      }
    });
  }, [lat, lng]);
  useEffect(() => {
    const overall = item?.metaData?.ratingBreakdown?.overall;
    if (overall) setListingRating(calculateRating(overall));
  }, [item]);
  useEffect(() => {
    if (!defaultCurrency?.code) return;
    setBaseCurrencyName(defaultCurrency.name);
    const amount = (item.rules?.price as Record<string, number> | undefined)?.[priceUnit];
    if (amount == null) return;
    const converted = currencyConvert(currencyList, item?.rules?.currency as string, defaultCurrency?.code, amount);
    if (converted) {
      const formatter = new NumberFormatService();
      setPrice(formatter.formatNumberWithCountingLabel(converted, languageCode));
    }
  }, [defaultCurrency, currencyList, item, priceUnit, languageCode]);
  const [target, setTarget] = useState<'_self' | '_blank'>('_self');
  const [rel, setRel] = useState('');
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth > 992) {
      setTarget('_blank');
      setRel('noopener noreferrer');
    }
  }, []);
  return <CardOnMap languageCode={languageCode} item={item} distance={distance} getdistance={getdistance} dir_rtl={languageCode === 'ar'} baseCurrencyName={baseCurrencyName} price_unit={priceUnit} price={price} listingRating={listingRating} rating_feature={ratingFeature} url={url} target={target} rel={rel} type={type} data-sentry-element="CardOnMap" data-sentry-component="LocationMapCard" data-sentry-source-file="LocationMapCard.tsx" />;
};
export default LocationMapCard;